import Attachments, { AttachmentType } from '@/components/Attachments';
import TagsInput from '@/components/TagsInput';
import Upload, { UploadType } from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import { usePrevious } from '@/lib/hooks';
import { Attachment } from '@/models/Attachment';
import { AvailableLanguage } from '@/models/AvailableLanguage';
import Category from '@/models/Category';
import { Tag } from '@/models/Item';
import { Lesson } from '@/models/Lesson';
import { LessonContentTO } from '@/models/LessonContentTO';
import { LessonData } from '@/models/LessonData';
import { LinkMetadata } from '@/models/LinkMetadata';
import { articleTpl } from '@/services/CourseService';
import api from '@/services/HttpService';
import { uploadHtml } from '@/services/S3Service';
import { ApplicationState } from '@/store';
import { postAttachmentRequest } from '@/store/attachments/action';
import { getCategoriesRequest } from '@/store/category/action';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import {
    deleteLessonRequest,
    getLessonRequest,
    postLessonRequest,
    putLessonRequest,
    setCurrentLessonForEdition,
    setLessonLinkMetadataRequest,
    setLessonLinkMetadataSuccess,
} from '@/store/lesson/action';
import { mdiZipBoxOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import ReactQuill from 'react-quill';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import _ from 'underscore';
import ContentType from '../ContentType';
import { DynamicAttribute, formInitialState, LessonForm, lessonSchema, TypeDynamic } from '../formConfig';
import LessonCardChart from './LessonCardChart';
import LessonCardDocument from './LessonCardDocument';
import './style.scss';

interface Props {
    lesson: Lesson;
    onDelete: CallableFunction;
    onSavePressed: boolean;
    onPrevPressed: boolean;
    onNextPressed: boolean;
    preRequisitesEnabled: boolean;
    lessonLength: number;
}

const LessonCard: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const {
        fetchedCategories,
        identity,
        course,
        strings,
        currentLessonInEdition,
        externalLinkMetadata,
        error,
        hasError,
        success,
        attachments,
    } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        currentContentLanguage: state.locale.currentContentLanguage,
        fetchedCategories: state.categories.data.categories,
        identity: state.identity.data,
        course: state.lists.courseData,
        currentLessonInEdition: state.lessons.currentData as Lesson,
        externalLinkMetadata: state.lessons.externalUrlMetadata,
        error: state.lessons.error,
        hasError: state.lessons.hasError,
        success: state.lessons.success,
        attachments: state.attachments.data,
    }));
    const { lesson, onDelete, onSavePressed, onPrevPressed, onNextPressed, preRequisitesEnabled, lessonLength } = props;
    const [isEdit, setIsEdit] = useState(false);
    const [isEditingAllowed, setIsEditingAllowed] = useState(false);
    const [canAddingSecondCategory, setCanAddingSecondCategory] = useState<boolean>(false);
    const [addingSecondCategory, setAddingSecondCategory] = useState<boolean>(false);
    const [newFileSelected, setNewFileSelected] = useState<boolean>(false);
    const [mainImagePreview, setMainImagePreview] = useState<string>();
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [fileOnQueueToConvert, setFileOnQueueToConvert] = useState<boolean>(false);
    const [formState, setFormState] = useState(formInitialState);
    const {
        mainImage,
        name,
        author,
        description,
        mandatory,
        prerequisites,
        categories,
        path,
        workload,
        tags,
        editorState,
        availableSubtitles,
    }: LessonForm = formState;
    const { dynamicAttributes }: TypeDynamic = formState;
    const [mediaType, setMediaType] = useState('video');
    const [mainImageDisabled, setMainImageDisabled] = useState<boolean>(false);
    const [descriptionDisabled, setDescriptionDisabled] = useState<boolean>(false);

    const prevCurrentLessonInEdition = usePrevious(currentLessonInEdition);

    const subtitlePath: string = availableSubtitles?.find((subtitle: AvailableLanguage) => subtitle.locale === course.locale)?.path || '';

    useEffect(() => {
        if (isEdit) {
            onCancelClickHandler();
        }
    }, [strings._LANGUAGE]);

    useEffect(() => {
        if (currentLessonInEdition && currentLessonInEdition.id && currentLessonInEdition.id === lesson.id) {
            setIsEdit(true);

            // dispatch(
            //     getAttachmentListRequest({
            //         locale: currentLessonInEdition.content.locale,
            //         entityId: currentLessonInEdition.content.id,
            //     } as AttachmentRequest),
            // );
        }
    }, []);

    useEffect(() => {
        if (isEdit) {
            lessonSchema
                .validate(formState, {
                    context: {
                        pathRequired: lesson.type !== Constants.lesson.type.ScormLesson && lesson.mediaType !== Constants.lesson.mediaType.HTML,
                        workloadRequired:
                            lesson.type === Constants.lesson.type.ScormLesson ||
                            [Constants.lesson.mediaType.HTML, Constants.lesson.mediaType.EXTERNAL].includes(lesson.mediaType),
                        editorStateRequired: lesson.mediaType === Constants.lesson.mediaType.HTML,
                    },
                })
                .then(() => {
                    if (lesson.mediaType !== Constants.lesson.mediaType.EXTERNAL) setFormIsValid(true);
                    else if (formState.dynamicAttributes !== formInitialState.dynamicAttributes) setFormIsValid(true);
                })
                .catch(() => setFormIsValid(false));
        }
    }, [isEdit, formState]);

    useEffect(() => {
        setFileOnQueueToConvert(
            lesson.mediaType === Constants.lesson.mediaType.VIDEO && !newFileSelected && !!path && !path.startsWith('/static') && isEdit,
        );
    }, [newFileSelected, path, isEdit]);

    useEffect(() => {
        if (isEdit) {
            const { orgId, applicationId } = identity.user;
            dispatch(getCategoriesRequest({ orgId, applicationId }));

            if (lesson.id) {
                dispatch(getLessonRequest(lesson.id));
            }
        }
    }, [dispatch, identity, isEdit]);

    const [checkConversionInterval, setCheckConversionInterval] = useState<NodeJS.Timeout | undefined>(undefined);
    const clearConversionInterval = (): void => {
        if (checkConversionInterval) {
            clearInterval(checkConversionInterval as NodeJS.Timeout);
            setCheckConversionInterval(undefined);
        }
    };

    useEffect(() => {
        if (lesson) {
            if (fileOnQueueToConvert && checkConversionInterval === undefined) {
                setCheckConversionInterval(
                    setInterval(() => {
                        dispatch(getLessonRequest(Number(lesson.id), true));
                    }, 3000),
                );
            } else {
                clearConversionInterval();
            }
        }
    }, [dispatch, fileOnQueueToConvert]);

    useEffect(() => {
        if (lesson.id) {
            //Exibição do card
            batchUpdateForm(lesson);
        } else {
            //Criação de nova lição
            setIsEdit(true);
            if (lesson.mediaType === Constants.lesson.mediaType.HTML) {
                handleOnChange('path', `media/${course.id}${new Date().getTime()}.html`);
            } else if (lesson.mediaType === 'EXTERNAL') {
                setMainImageDisabled(true);
                setDescriptionDisabled(true);
            }

            if (course.categories) {
                setCanAddingSecondCategory(true);

                if (course.categories.length > 1) {
                    setAddingSecondCategory(true);
                }
                handleOnChange('categories', course.categories);
            }

            if (course.tags) {
                handleOnChange('tags', course.tags);
            }

            const lesonsElements: HTMLDivElement[] = (document.getElementsByClassName('lesson-card') as unknown) as HTMLDivElement[];
            const newLessonElement: HTMLDivElement = (lesonsElements[lesonsElements.length - 1] as unknown) as HTMLDivElement;

            window.scroll({
                behavior: 'smooth',
                left: 0,
                top: getOffsetTop(newLessonElement) - newLessonElement.clientHeight,
            });

            setMediaType(lesson.mediaType === Constants.lesson.mediaType.VIDEO ? strings.VIDEO_TYPE_NAME : strings.AUDIO_TYPE_NAME);
        }
    }, [lesson]);

    useEffect(() => {
        return () => {
            clearConversionInterval();
        };
    }, []);

    useEffect(() => {
        setIsEditingAllowed(!currentLessonInEdition || currentLessonInEdition.id === lesson.id ? true : false);

        if (currentLessonInEdition && currentLessonInEdition.id && currentLessonInEdition.id === lesson.id) {
            //Carregando os campos para edição da lição
            if (!_.isEqual(prevCurrentLessonInEdition, currentLessonInEdition)) {
                if (prevCurrentLessonInEdition && prevCurrentLessonInEdition.path !== currentLessonInEdition.path) {
                    handleOnChange('path', currentLessonInEdition.path);
                } else {
                    batchUpdateForm(currentLessonInEdition);
                }
            }
        }
    }, [currentLessonInEdition, lesson]);

    useEffect(() => {
        if (currentLessonInEdition) {
            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveDisabled: !formIsValid,
                    previewDisabled: !formIsValid,
                } as FloatingCourseActionsBarConfiguration),
            );
        }
    }, [formIsValid, currentLessonInEdition]);

    useEffect(() => {
        if (lesson.mediaType === 'EXTERNAL' && isEdit && externalLinkMetadata) {
            // Habilita os campos caso não existam metadados
            if (externalLinkMetadata.image) {
                setMainImageDisabled(true);
            } else {
                setMainImageDisabled(false);
            }
            if (externalLinkMetadata.description) {
                setDescriptionDisabled(true);
            } else {
                setDescriptionDisabled(false);
            }
            // Carrega a imagem e descrição da página externa na lição
            handleOnChange('mainImage', externalLinkMetadata.image);
            handleOnChange('description', externalLinkMetadata.description);
        }
    }, [externalLinkMetadata]);

    useEffect(() => {
        if (error && error.message === 'lessonLinkMetadataRequestFailure') {
            setMainImageDisabled(false);
            setDescriptionDisabled(false);
        }
    }, [hasError]);

    const batchUpdateForm = (editingLesson: Lesson) => {
        handleOnChange('mainImage', editingLesson.mainImage);
        handleOnChange('name', editingLesson.name);
        handleOnChange('author', editingLesson.content.author);
        handleOnChange('description', editingLesson.description);
        handleOnChange('locale', editingLesson.content.locale);
        handleOnChange('mandatory', editingLesson.mandatory);
        handleOnChange('prerequisites', editingLesson.prerequisites);
        handleOnChange('dynamicAttributes', editingLesson.dynamicAttributes);
        handleOnChange('path', editingLesson.path);
        handleOnChange('workload', editingLesson.workload);
        handleOnChange('availableSubtitles', editingLesson.content.availableSubtitles);

        if (editingLesson.mediaType === Constants.lesson.mediaType.HTML && lesson.id && isEdit) {
            getArticleContent();
            handleOnChange('path', `media/${course.id}${new Date().getTime()}.html`);
        }

        if (editingLesson.content.categories) {
            if (editingLesson.content.categories.length > 1) {
                setAddingSecondCategory(true);
            }
            handleOnChange('categories', editingLesson.content.categories);
        }

        if (editingLesson.content.tags) {
            handleOnChange('tags', editingLesson.content.tags);
        }
    };

    const handleOnChange = useCallback(
        (
            fieldName: string,
            value: string | number | boolean | object | Tag[] | null | undefined | DynamicAttribute | AvailableLanguage[],
            index?: number,
        ) => {
            if (fieldName === 'categories' && index !== undefined) {
                const currentCategory = fetchedCategories && fetchedCategories.find((category: Category) => category.id === Number(value));
                const tmpCategories: Category[] = [...categories];

                if (currentCategory) {
                    tmpCategories[Number(index)] = currentCategory;
                } else {
                    tmpCategories.splice(Number(index), 1);
                }

                setCanAddingSecondCategory(tmpCategories.length > 0);
                value = tmpCategories;
            }

            if (fieldName === 'workload') {
                value = Number(value) > 0 ? value : 0;
            }

            if (fieldName === 'path' && lesson.mediaType !== Constants.lesson.mediaType.HTML) {
                if (!value) {
                    setFormState((prevState) => ({ ...prevState, [fieldName]: formInitialState.path, workload: formInitialState.workload }));
                    return;
                }
                if (lesson.mediaType === 'EXTERNAL' && isEdit) {
                    setFormIsValid(false); //Desabilitando botões enquanto carrega os metadados

                    const pattern = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/g;

                    if (String(value).match(pattern)) {
                        dispatch(setLessonLinkMetadataRequest(String(value)));
                    }
                }
            }

            if (fieldName === 'editorState' && value === '<p><br></p>') {
                value = '';
            }

            if (fieldName === 'availableSubtitles' && !!value) {
                const availableSubtitlesInEdition: AvailableLanguage[] = currentLessonInEdition?.content?.availableSubtitles || [];

                (value as AvailableLanguage[]).forEach((availableSubtitleValue: AvailableLanguage) => {
                    const subtitleId: number | undefined = availableSubtitlesInEdition?.find(
                        (availableSubtitleInEdtion: AvailableLanguage) => availableSubtitleValue.locale === availableSubtitleInEdtion.locale,
                    )?.id;

                    if (!!subtitleId) {
                        availableSubtitleValue.id = subtitleId;
                    }
                });
            }

            setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
        },
        [fetchedCategories, categories],
    );

    const renderCategoriesOptions = (categoryIndex: number) => {
        if (fetchedCategories) {
            const nonCurrentIndex = categories.findIndex((cat: Category, index: number) => categoryIndex !== index);

            return fetchedCategories
                .filter((category: Category) => !categories[nonCurrentIndex] || categories[nonCurrentIndex].id !== category.id)
                .map((category: Category) => (
                    <option key={category.id} value={category.id}>
                        {category.name}
                    </option>
                ));
        }
        return null;
    };

    const getLessonTypeIcon = () => {
        if (lesson.type === Constants.lesson.type.ScormLesson) {
            return <Icon horizontal path={mdiZipBoxOutline} size={1} />;
        }

        switch (lesson.mediaType) {
            case Constants.lesson.mediaType.HTML:
                return <i className="material-icons-outlined align-middle">{Constants.icons.FILE_DOCUMENT_OUTLINE}</i>;
            case Constants.lesson.mediaType.VIDEO:
                return <i className="material-icons-outlined align-middle">{Constants.icons.VIDEOCAM}</i>;
            case Constants.lesson.mediaType.AUDIO:
                return <i className="material-icons-outlined align-middle">{Constants.icons.MICROPHONE}</i>;
            case Constants.lesson.mediaType.EXTERNAL:
                return <i className="material-icons-outlined align-middle">{Constants.icons.LINK}</i>;
            case Constants.lesson.mediaType.DOCUMENT:
                return <i className="material-icons-outlined align-middle">{Constants.icons.FILE_COPY}</i>;
            case Constants.lesson.mediaType.CHART:
                return <i className="material-icons-outlined align-middle">{Constants.icons.IMAGE_OUTLINE}</i>;
            default:
                return <i className="material-icons-outlined align-middle">{Constants.icons.VIDEOCAM}</i>;
        }
    };

    const onEditClickHandler = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsEdit(true);
        dispatch(setCurrentLessonForEdition(lesson));
    };

    useEffect(() => {
        if (lessonLength == 1) setFormState((formState) => ({ ...formState, prerequisites: false }));
    }, [lessonLength]);

    const saveLesson = async () => {
        if (lesson.mediaType === Constants.lesson.mediaType.EXTERNAL) {
            if (!path.startsWith('http')) {
                toast.error(strings.toastMessages.ADD_CONTENT_URL_ERROR);
                return;
            }
        }

        if (lesson.mediaType === Constants.lesson.mediaType.HTML && editorState) {
            await uploadHtml(articleTpl.replace('{0}', editorState), path);
        }
        if (lesson.id) {
            dispatch(putLessonRequest(({ ...lesson, ...formState, videoUpdate: newFileSelected } as unknown) as LessonContentTO));
        } else {
            dispatch(
                postLessonRequest(({
                    mediaLesson: {
                        ...lesson,
                        ...formState,
                        course: { id: course.id },
                        locale: course.locale,
                        applicationId: Constants.APP_ID,
                        organizationId: identity.user.orgId,
                    },
                } as unknown) as LessonData),
            );
        }
    };

    const onSaveClickHandler = (e: SyntheticEvent) => {
        setFormIsValid(false); //impedindo que mais de uma lição seja criada em caso de cliques rápidos
        e.preventDefault();
        e.stopPropagation();
        saveLesson();
        clearConversionInterval();
    };

    async function getArticleContent() {
        const articlePath: string | undefined = lesson.path;
        if (articlePath) {
            const { data } = await api.get(articlePath);
            handleOnChange('editorState', data);
        }
    }

    useEffect(() => {
        if (isEdit && (onNextPressed || onPrevPressed || onSavePressed)) {
            saveLesson();
        } else {
            redirect();
        }
    }, [onNextPressed, onPrevPressed, onSavePressed]);

    useEffect(() => {
        if (success && isEdit) {
            setIsEdit(false);

            dispatch(setLessonLinkMetadataSuccess({} as LinkMetadata));
            dispatch(setCurrentLessonForEdition(undefined));
            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveDisabled: false,
                    previewDisabled: false,
                } as FloatingCourseActionsBarConfiguration),
            );
        }
    }, [success, isEdit]);

    const redirect = () => {
        if (onSavePressed) {
            history.push('/contents/lms/courses');
        }
        if (onPrevPressed) {
            history.push(`/contents/lms/courses/${course.id}`);
        }
    };

    const onDeleteClickHandler = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        clearConversionInterval();

        if (lesson.id) {
            dispatch(deleteLessonRequest(lesson.id));
            onDelete(lesson.id);
        } else onDelete();
    };

    const onCancelClickHandler = (e?: SyntheticEvent) => {
        e && e.preventDefault();
        e && e.stopPropagation();

        clearConversionInterval();

        setIsEdit(false);

        dispatch(setLessonLinkMetadataSuccess(undefined));
        dispatch(setCurrentLessonForEdition(undefined));

        dispatch(
            setFloatingCourseActionsConfiguration({
                saveDisabled: true,
                previewDisabled: false,
            } as FloatingCourseActionsBarConfiguration),
        );

        if (!lesson.id) {
            onDelete();
        }
    };

    const getOffsetTop = (elem: HTMLElement) => {
        let offsetLeft: number = 0;
        do {
            if (!isNaN(elem.offsetLeft)) {
                offsetLeft += elem.offsetTop;
            }
        } while ((elem = elem.offsetParent as HTMLElement));

        return offsetLeft;
    };

    const handleRemove = () => {
        setAddingSecondCategory(false);

        const categoriesUpdated: Category[] = [...categories];
        categoriesUpdated.splice(1, 1);
        handleOnChange('categories', categoriesUpdated);
    };

    const addAttachment = (fileUploadData: FileUploadData) => {
        const { fileName, key } = fileUploadData;

        dispatch(
            postAttachmentRequest({
                name: fileName,
                contentId: lesson.content.id,
                lessonId: lesson.id,
                path: key,
                locale: lesson.content.locale,
                target: 'student',
            } as Attachment),
        );
    };

    return (
        <>
            {!isEdit && (
                <div className="main">
                    <div className="lesson-card">
                        <div className="lesson-card__body">
                            <div>
                                <span className="lesson-card__label">{strings.LESSON}</span>
                                <span className="lesson-card__icon">{getLessonTypeIcon()}</span>
                            </div>
                            <h3 className="lesson-card__title" title={name}>
                                {name || strings.NO_TITLE}
                            </h3>
                            <p className="lesson-card__text" title={description}>
                                {description || strings.NO_DESCRIPTION}
                            </p>
                        </div>
                        <div className="lesson-card__edit-container">
                            <div className="lesson-card__edit-container__thumb">
                                {mainImage && <img alt="" className="lesson-card__edit-container__thumb__img" src={mainImagePreview || mainImage} />}
                                {!mainImage && (
                                    <div className="lesson-card__edit-container__empty-thumb">
                                        <div>
                                            <i className="material-icons icon">{Constants.icons.SCHOOL}</i>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="edit__author-container">
                            <div className="author">
                                <div className="lesson-card__author">{author || strings.DEFAULT_AUTHOR}</div>
                                <div className="lesson-card__duration">{`${workload || 0} ${strings.MINUTES}`}</div>
                            </div>

                            {isEditingAllowed && (
                                <div className="lesson-card__edit-container__edit ">
                                    <a href="#" onClick={onEditClickHandler}>
                                        <span className="edit-btn">{strings.EDIT}</span>
                                        <i className="material-icons icon edit-icon">{Constants.icons.EDIT}</i>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isEdit && (
                <div className="main">
                    <div className="lesson-card ">
                        <div className="lesson-card__body">
                            <div>
                                <span className="lesson-card__label">{strings.LESSON}</span>
                                <span className="lesson-card__icon">{getLessonTypeIcon()}</span>
                            </div>

                            <input
                                className="lesson-card__title edit edit__title "
                                id="titleInput"
                                onChange={(e) => handleOnChange('name', e.target.value)}
                                placeholder={strings.NO_TITLE}
                                required
                                type="text"
                                value={name || ''}
                            />

                            {descriptionDisabled && !description && (
                                <div className="lesson-card__text edit edit__description disabled fake-description">
                                    {strings.NO_DESCRIPTION}{' '}
                                    <span className="lesson-card-hint">
                                        <OverlayTrigger
                                            overlay={<Tooltip id="lesson-card-hint">{strings.LESSON_DESCRIPTION_DISABLED_INFO}</Tooltip>}
                                            placement="bottom"
                                        >
                                            <i className="material-icons-outlined hint" style={{ width: 14 }}>
                                                {Constants.icons.INFO_OUTLINE}
                                            </i>
                                        </OverlayTrigger>
                                    </span>
                                </div>
                            )}
                            {(!descriptionDisabled || description) && (
                                <input
                                    className="lesson-card__text edit edit__description"
                                    disabled={descriptionDisabled}
                                    id="descriptionInput"
                                    onChange={(e) => handleOnChange('description', e.target.value)}
                                    placeholder={strings.NO_DESCRIPTION}
                                    required
                                    type="text"
                                    value={descriptionDisabled && !description ? strings.LESSON_DESCRIPTION_DISABLED_INFO : description || ''}
                                />
                            )}
                        </div>

                        <div className="lesson-card__edit-container">
                            <div className={`lesson-card__edit-container__upload-thumb ${mainImageDisabled ? 'disabled' : ''}`}>
                                {!(mainImagePreview || mainImage) && (
                                    <Upload
                                        disabled={mainImageDisabled}
                                        onComplete={(response: FileUploadData) => {
                                            handleOnChange('mainImage', response.key);
                                            setMainImagePreview(response.preview);
                                        }}
                                        onRemove={() => handleOnChange('mainImage', undefined)}
                                        typeAccepted="IMAGE"
                                        uploadType={UploadType.LESSON_THUMB}
                                    />
                                )}
                                {(mainImagePreview || mainImage) && (
                                    <img alt="" className="lesson-card__edit-container__thumb__img" src={mainImagePreview || mainImage} />
                                )}
                            </div>
                        </div>

                        <div className="edit__author-container">
                            <div className="lesson-card__edit-container__author">
                                <input
                                    className="lesson-card__author edit edit__author"
                                    id="authorInput"
                                    onChange={(e) => handleOnChange('author', e.target.value)}
                                    placeholder={strings.DEFAULT_AUTHOR}
                                    required
                                    type="text"
                                    value={author || ''}
                                />

                                {[Constants.lesson.mediaType.VIDEO, Constants.lesson.mediaType.AUDIO].includes(lesson.mediaType) && (
                                    <div className="lesson-card__duration">
                                        {`${workload || 0} ${strings.MINUTES}`}
                                        <span className="lesson-card-hint">
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip id="lesson-card-hint">{strings.MEDIA_MINUTES_ALERT.replace('%s', mediaType)}</Tooltip>
                                                }
                                                placement="bottom"
                                            >
                                                <i className="material-icons-outlined">{Constants.icons.ERROR_OUTLINE}</i>
                                            </OverlayTrigger>
                                        </span>
                                    </div>
                                )}
                                {![Constants.lesson.mediaType.VIDEO, Constants.lesson.mediaType.AUDIO].includes(lesson.mediaType) && (
                                    <>
                                        <input
                                            className="lesson-card__duration edit edit__duration"
                                            id="timeInput"
                                            min="0"
                                            onChange={(e) => handleOnChange('workload', parseInt(e.target.value))}
                                            required
                                            type="number"
                                            value={workload || 0}
                                        />
                                        <span className="lesson-card__duration"> {strings.MINUTES}</span>
                                    </>
                                )}
                            </div>

                            {currentLessonInEdition && currentLessonInEdition.id && lesson.type !== Constants.lesson.type.ScormLesson && (
                                <div className="lesson-card__edit-container__delete">
                                    {!course.published && (
                                        <a href="#" onClick={onDeleteClickHandler}>
                                            <span className="delete-btn">{strings.DELETE}</span>
                                        </a>
                                    )}
                                </div>
                            )}

                            <div className="lesson-card__edit-container__cancel">
                                <a href="#" onClick={onCancelClickHandler}>
                                    <span className="cancel-btn">{strings.CANCEL}</span>
                                </a>
                            </div>

                            <div className="lesson-card__edit-container__save">
                                <button className="btn save-btn" disabled={!formIsValid} onClick={onSaveClickHandler}>
                                    <span className="save-btn">{strings.SAVE}</span>
                                    <i className="material-icons-outlined icon save-icon">{Constants.icons.SAVE}</i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="edition-card">
                        {lesson.mediaType === Constants.lesson.mediaType.VIDEO && (
                            <>
                                <div className="input-section">
                                    <h1>{strings.VIDEO_FILE}</h1>
                                    <Upload
                                        fileOnQueueToConvert={fileOnQueueToConvert}
                                        onComplete={(e) => {
                                            setNewFileSelected(true);
                                            handleOnChange('path', e.key);
                                        }}
                                        onLoadedMetadata={(minutes: number) => handleOnChange('workload', minutes)}
                                        onRemove={() => handleOnChange('path', null)}
                                        previewFile={{ uri: path, name: path }}
                                        typeAccepted="VIDEO"
                                    />
                                </div>

                                <div className="input-section">
                                    <h1>{strings.HAS_SUBTITLES}</h1>
                                    <Upload
                                        onComplete={(e) => handleOnChange('availableSubtitles', [{ path: e.key, locale: course.locale }])}
                                        onRemove={() => handleOnChange('availableSubtitles', undefined)}
                                        previewFile={{ uri: subtitlePath, name: subtitlePath }}
                                        typeAccepted="SUBTITLE"
                                    />
                                </div>
                            </>
                        )}

                        {lesson.mediaType === Constants.lesson.mediaType.HTML && (
                            <>
                                <div className="input-section">
                                    <h1>{strings.ARTICLE_CONTENT}</h1>
                                </div>
                                <div className="editor">
                                    <ReactQuill
                                        defaultValue={editorState || ''}
                                        formats={Constants.reactQuillAvailableFormats}
                                        onChange={(val) => handleOnChange('editorState', val)}
                                        placeholder={strings.TYPE_TEXT}
                                        style={{
                                            width: '100%',
                                            background: '#fff',
                                            color: '#808080',
                                            fontFamily: '"Exo 2", sans-serif',
                                            fontSize: '18px',
                                        }}
                                        theme="snow"
                                        value={editorState || ''}
                                    />
                                </div>
                            </>
                        )}

                        {lesson.mediaType === Constants.lesson.mediaType.AUDIO && (
                            <>
                                <div className="input-section">
                                    <h1>{strings.AUDIO_FILE}</h1>
                                    <Upload
                                        fileOnQueueToConvert={fileOnQueueToConvert}
                                        onComplete={(e) => {
                                            setNewFileSelected(true);
                                            handleOnChange('path', e.key);
                                        }}
                                        onLoadedMetadata={(minutes: number) => handleOnChange('workload', minutes)}
                                        onRemove={() => handleOnChange('path', null)}
                                        previewFile={{ uri: path, name: path }}
                                        typeAccepted="AUDIO"
                                    />
                                </div>
                            </>
                        )}
                        {lesson.mediaType === Constants.lesson.mediaType.EXTERNAL && (
                            <>
                                <div className="input-section">
                                    <h1>{strings.CONTENT_TYPE}</h1>
                                    <div className="content-types">
                                        <div className="row-content">
                                            <ContentType setType={handleOnChange} type={dynamicAttributes} />
                                        </div>
                                    </div>
                                    <div className="input-section">
                                        <h1>Link</h1>
                                        <input
                                            className="form-control content-link"
                                            onChange={(e) => {
                                                setNewFileSelected(true);
                                                handleOnChange('path', e.target.value);
                                            }}
                                            placeholder={strings.EXTERNAL_LINK}
                                            required
                                            type="url"
                                            value={path || ''}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {lesson.mediaType === Constants.lesson.mediaType.DOCUMENT && <LessonCardDocument onChange={handleOnChange} path={path} />}

                        {lesson.mediaType === Constants.lesson.mediaType.CHART && <LessonCardChart onChange={handleOnChange} path={path} />}

                        <div className="input-section edition-card__checkbox-container">
                            <div className="pre-req d-flex">
                                <div
                                    className={`check-area ${!preRequisitesEnabled ? 'div-disabled' : ''}`}
                                    onClick={() => preRequisitesEnabled && handleOnChange('prerequisites', !prerequisites)}
                                >
                                    <i
                                        className={`material-icons ${prerequisites && preRequisitesEnabled ? 'checked' : 'check'} ${
                                            !preRequisitesEnabled ? 'div-disabled' : ''
                                        }`}
                                    >
                                        {prerequisites && preRequisitesEnabled ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                    </i>
                                    <span> {strings.HAS_PREREQUISITE} </span>
                                </div>
                                <span>
                                    <OverlayTrigger
                                        overlay={<Tooltip id="prerequisite-hint">{strings.TOOLTIP_HAS_PREREQUISITE}</Tooltip>}
                                        placement="bottom"
                                    >
                                        <i className="material-icons-outlined align-middle hint">{Constants.icons.ERROR_OUTLINE}</i>
                                    </OverlayTrigger>
                                </span>
                            </div>
                            <div className="d-flex">
                                <div className="check-area" onClick={() => handleOnChange('mandatory', !mandatory)}>
                                    <i className={`material-icons ${mandatory ? 'checked' : 'check'}`}>
                                        {mandatory ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                    </i>
                                    <span> {strings.ITS_MANDATORY} </span>
                                </div>
                                <span>
                                    <OverlayTrigger
                                        overlay={<Tooltip id="mandatory-hint">{strings.TOOLTIP_ITS_MANDATORY}</Tooltip>}
                                        placement="bottom"
                                    >
                                        <i className="material-icons-outlined align-middle hint">{Constants.icons.ERROR_OUTLINE}</i>
                                    </OverlayTrigger>
                                </span>
                            </div>
                        </div>

                        <div className="input-section">
                            <div className="field-title-container">
                                <h1>{strings.ATTACHMENTS}</h1>
                            </div>

                            {lesson && lesson.content ? (
                                <>
                                    <Attachments content={lesson.content} locale={lesson.content?.locale} type={AttachmentType.ADMIN} />
                                    <Upload fileNameLimitSize={100} onComplete={addAttachment} typeAccepted="ATTACHMENTS" />
                                    <div className="order-upload-message">
                                        <span>{strings.ORDER_ATTACHMENTS_UPLOADED_MESSAGE}</span>
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-warning" role="alert">
                                    <i className="material-icons align-middle">warning</i>
                                    <span>{strings.ATTACHMENTS_AFTER_SAVING_LESON} </span>
                                </div>
                            )}
                        </div>

                        <div className="input-section">
                            <div className="field-title-container">
                                <h1>{strings.CATEGORY} </h1>
                                <span>
                                    <OverlayTrigger overlay={<Tooltip id="category-hint">{strings.TOOLTIP_CATEGORY}</Tooltip>} placement="bottom">
                                        <i className="material-icons-outlined align-middle hint">{Constants.icons.ERROR_OUTLINE}</i>
                                    </OverlayTrigger>
                                </span>
                            </div>
                            <div className="categories">
                                <select
                                    className="form-control"
                                    name="category1"
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleOnChange('categories', e.target.value, 0)}
                                    value={categories && categories[0] ? categories[0].id : ''}
                                >
                                    <option value={strings.SELECT_CATEGORY}>{strings.SELECT_CATEGORY}</option>
                                    {renderCategoriesOptions(0)}
                                </select>
                                {addingSecondCategory ? (
                                    <div className="categories__second">
                                        <select
                                            className="form-control"
                                            name="category2"
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleOnChange('categories', e.target.value, 1)}
                                            value={categories && categories[1] ? categories[1].id : ''}
                                        >
                                            <option value={strings.SELECT_CATEGORY}>{strings.SELECT_CATEGORY}</option>
                                            {renderCategoriesOptions(1)}
                                        </select>
                                        <button className="btn btn-link" onClick={() => handleRemove()}>
                                            <i className="material-icons-outlined align-middle">{Constants.icons.DELETE}</i>
                                            {strings.REMOVE}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="d-flex">
                                        {canAddingSecondCategory && (
                                            <button className="btn btn-link" onClick={() => setAddingSecondCategory(true)}>
                                                <span>{strings.ADD_CATEGORY}</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="input-section">
                            <div className="field-title-container">
                                <h1>{strings.TAGS}</h1>
                                <span>
                                    <OverlayTrigger overlay={<Tooltip id="tags-hint">{strings.TOOLTIP_TAGS}</Tooltip>} placement="bottom">
                                        <i className="material-icons-outlined align-middle hint">{Constants.icons.ERROR_OUTLINE}</i>
                                    </OverlayTrigger>
                                </span>
                            </div>
                            <div className="tags">
                                <TagsInput selectedTags={tags} setTags={handleOnChange} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LessonCard;
